import { MARKET_API_URL } from "../config";
import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

export const MarketContext = createContext();

export const MarketProvider = ({ children }) => {
  const [ticks, setTicks] = useState({
    "NIFTY": { price: 24500.0, percentChange: "+0.45%", isDown: false },
    "SENSEX": { price: 80450.0, percentChange: "+0.32%", isDown: false },
    "BANKNIFTY": { price: 52300.0, percentChange: "-0.15%", isDown: true }
  });
  const [subscribedSymbols, setSubscribedSymbols] = useState(new Set([
    "NIFTY", "SENSEX", "BANKNIFTY", "RELIANCE", "HDFCBANK", "INFY", "TCS", "BHARTIARTL", "ITC", "SBIN"
  ]));
  const [wsConnected, setWsConnected] = useState(false);

  const socketRef = useRef(null);

  const addSubscribedSymbols = useCallback((symbols) => {
    setSubscribedSymbols((prev) => {
      const next = new Set(prev);
      let changed = false;
      symbols.forEach((s) => {
        if (s && !next.has(s)) {
          next.add(s);
          changed = true;
        }
      });
      if (changed && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          action: "subscribe",
          symbols: Array.from(next)
        }));
      }
      return changed ? next : prev;
    });
  }, []);

  // ─── WebSocket Connection for Real-Time Live Tick Stream ──────────────────
  useEffect(() => {
    let ws;
    const connectWs = () => {
      try {
        ws = new WebSocket(MARKET_API_URL.replace(/^http/, "ws") + "/ws/ticks");
        socketRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          // Subscribe initial symbol list
          ws.send(JSON.stringify({
            action: "subscribe",
            symbols: Array.from(subscribedSymbols)
          }));
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === "ticks" && msg.data) {
              setTicks((prev) => {
                const updated = { ...prev };
                Object.entries(msg.data).forEach(([sym, newPrice]) => {
                  const old = prev[sym] || { price: newPrice };
                  const isDown = newPrice < old.price;
                  const pct = old.price > 0 ? (((newPrice - old.price) / old.price) * 100).toFixed(2) : "0.00";
                  updated[sym] = {
                    price: newPrice,
                    prevPrice: old.price,
                    percentChange: `${pct >= 0 ? '+' : ''}${pct}%`,
                    isDown,
                    updatedAt: Date.now()
                  };
                });
                return updated;
              });
            }
          } catch (e) {
            console.error("Error parsing WS message", e);
          }
        };

        ws.onerror = () => {
          setWsConnected(false);
        };

        ws.onclose = () => {
          setWsConnected(false);
          // Reconnect after 3 seconds
          setTimeout(connectWs, 3000);
        };
      } catch (e) {
        setWsConnected(false);
      }
    };

    connectWs();

    return () => {
      if (ws) ws.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Fallback HTTP Polling (Runs only if WebSocket is disconnected) ────────
  const fetchLiveTicksHTTP = useCallback(async () => {
    if (wsConnected) return; // Skip HTTP polling if WebSocket is healthy
    const symbolList = Array.from(subscribedSymbols);
    if (symbolList.length === 0) return;

    try {
      const tickUpdates = {};
      for (const sym of symbolList.slice(0, 15)) {
        try {
          const res = await axios.get(`${MARKET_API_URL}/api/smartapi/ltp?symbol=${sym}`, { timeout: 2000 });
          if (res.data && res.data.price) {
            const newPrice = res.data.price;
            tickUpdates[sym] = (prevTicks) => {
              const old = prevTicks[sym] || { price: newPrice };
              const isDown = newPrice < old.price;
              const pct = old.price > 0 ? (((newPrice - old.price) / old.price) * 100).toFixed(2) : "0.00";
              return {
                price: newPrice,
                prevPrice: old.price,
                percentChange: `${pct >= 0 ? '+' : ''}${pct}%`,
                isDown,
                updatedAt: Date.now()
              };
            };
          }
        } catch (e) {}
      }

      setTicks((prev) => {
        const updated = { ...prev };
        Object.keys(tickUpdates).forEach((sym) => {
          updated[sym] = tickUpdates[sym](prev);
        });
        return updated;
      });
    } catch (err) {
      console.error("Error updating market ticks via HTTP fallback", err);
    }
  }, [subscribedSymbols, wsConnected]);

  useEffect(() => {
    if (!wsConnected) {
      fetchLiveTicksHTTP();
      const interval = setInterval(fetchLiveTicksHTTP, 3000);
      return () => clearInterval(interval);
    }
  }, [fetchLiveTicksHTTP, wsConnected]);

  const getLtp = (symbol, fallbackPrice = 0) => {
    if (!symbol) return fallbackPrice;
    const cleanSym = symbol.replace("-EQ", "");
    return ticks[cleanSym]?.price || ticks[symbol]?.price || fallbackPrice;
  };

  return (
    <MarketContext.Provider value={{ ticks, getLtp, addSubscribedSymbols, wsConnected }}>
      {children}
    </MarketContext.Provider>
  );
};
